import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function Printer() {
  const navigate = useNavigate();
  const [step, setStep]             = useState('booths');
  const [booths, setBooths]         = useState([]);
  const [voters, setVoters]         = useState([]);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [loadingBooths, setLoadingBooths] = useState(true);
  const [loadingVoters, setLoadingVoters] = useState(false);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [total, setTotal]           = useState(0);
  const [search, setSearch]         = useState('');

  useEffect(() => {
    axios.get('/stats/booths')
      .then(r => setBooths(r.data))
      .catch(() => toast.error('Failed to load booths'))
      .finally(() => setLoadingBooths(false));
  }, []);

  const loadVoters = async (boothNumber, p = 1) => {
    setLoadingVoters(true);
    try {
      const { data } = await axios.get(`/voters/booth/${boothNumber}`, { params:{ page:p, limit:50 } });
      setVoters(data.voters || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
      setSelectedBooth(boothNumber);
      setStep('voters');
    } catch { toast.error('Failed to load voters'); }
    finally { setLoadingVoters(false); }
  };

  const handleSearch = async () => {
    if (!search.trim()) { toast.error('Enter a name or booth number'); return; }
    setLoadingVoters(true);
    try {
      const isNum = /^\d+$/.test(search.trim());
      const params = isNum ? { booth_number: search.trim(), limit:50 } : { name: search.trim(), limit:50 };
      const { data } = await axios.get('/voters/search', { params });
      setVoters(data.voters || []);
      setTotal(data.total || 0);
      setPages(1); setPage(1);
      setSelectedBooth(null);
      setStep('voters');
      if (!data.voters?.length) toast('No voters found', { icon:'🔍' });
    } catch { toast.error('Search failed'); }
    finally { setLoadingVoters(false); }
  };

  const filteredBooths = booths.filter(b =>
    !search || b.booth_number?.toString().includes(search) ||
    b.booth_address?.toLowerCase().includes(search.toLowerCase()) ||
    b.area?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(160deg,#FFF4ED,#FFF8F0)', fontFamily:"'Outfit',sans-serif" }}>

      {/* Header */}
      <div className="app-header">
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button className="back-btn" onClick={() => step==='voters' ? (setStep('booths'), setVoters([]), setSearch('')) : navigate('/')}>←</button>
          <div>
            <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'1.1rem', color:'white' }}>
              {step==='booths' ? '🖨️ Printer' : `🏫 Booth ${selectedBooth||'Search'} — ${total} voters`}
            </div>
            <div style={{ fontSize:'0.68rem', opacity:.85, color:'white' }}>
              {step==='booths' ? 'Select booth to print voter slip' : 'Select voter to print'}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth:700, margin:'0 auto', padding:'16px 16px 80px' }}>

        {/* Search Bar */}
        {step==='booths' && (
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <input
              style={{ flex:1, padding:'12px 16px', border:'2px solid rgba(255,107,0,0.2)', borderRadius:14, fontSize:'0.88rem', fontFamily:"'Outfit',sans-serif", outline:'none', background:'rgba(255,255,255,0.9)', transition:'all 0.2s' }}
              placeholder="🔍 Search by name, booth no or area..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key==='Enter' && handleSearch()}
              onFocus={e=>{e.target.style.borderColor='#FF6B00';e.target.style.boxShadow='0 0 0 3px rgba(255,107,0,0.1)';}}
              onBlur={e=>{e.target.style.borderColor='rgba(255,107,0,0.2)';e.target.style.boxShadow='none';}}
            />
            <button onClick={handleSearch} style={{ padding:'12px 18px', border:'none', borderRadius:14, background:'linear-gradient(135deg,#E55100,#FF6B00)', color:'white', fontWeight:700, fontSize:'0.88rem', cursor:'pointer', fontFamily:"'Outfit',sans-serif", whiteSpace:'nowrap' }}>
              Search
            </button>
          </div>
        )}

        {/* BOOTH LIST */}
        {step==='booths' && (
          loadingBooths
            ? <div className="loader"><div className="spinner"/></div>
            : filteredBooths.length === 0
              ? <div style={{ textAlign:'center', padding:40, color:'#9E6040' }}>
                  <div style={{ fontSize:'2.5rem', marginBottom:10 }}>🔍</div>
                  <div style={{ fontWeight:700 }}>No booths found</div>
                </div>
              : <div className="stagger">
                  {filteredBooths.map((b, idx) => (
                    <div key={`${b.booth_number}-${idx}`} onClick={() => loadVoters(b.booth_number)}
                      style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 18px', borderRadius:16, marginBottom:10, background:'rgba(255,255,255,0.9)', border:'1.5px solid rgba(255,107,0,0.12)', cursor:'pointer', transition:'all 0.25s', boxShadow:'0 3px 14px rgba(255,107,0,0.06)' }}
                      onMouseEnter={e=>{e.currentTarget.style.transform='translateX(6px)';e.currentTarget.style.boxShadow='0 8px 28px rgba(255,107,0,0.18)';e.currentTarget.style.borderColor='rgba(255,107,0,0.35)';}}
                      onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 3px 14px rgba(255,107,0,0.06)';e.currentTarget.style.borderColor='rgba(255,107,0,0.12)';}}>
                      <div style={{ width:48, height:48, borderRadius:14, background:'linear-gradient(135deg,#E55100,#FF6B00)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:'1rem', flexShrink:0 }}>
                        {b.booth_number}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:'0.92rem', color:'#1A0A00', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                          {b.booth_address || `Booth ${b.booth_number}`}
                        </div>
                        {b.area && <div style={{ fontSize:'0.76rem', color:'#9E6040', marginTop:2 }}>📍 {b.area}</div>}
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:800, fontSize:'1.1rem', color:'#E55100' }}>{b.total||0}</div>
                        <div style={{ fontSize:'0.7rem', color:'#9E6040' }}>{b.male||0}M / {b.female||0}F</div>
                      </div>
                      <span style={{ color:'#FF6B00', fontSize:'1.2rem', marginLeft:4 }}>›</span>
                    </div>
                  ))}
                </div>
        )}

        {/* VOTER LIST */}
        {step==='voters' && (
          loadingVoters
            ? <div className="loader"><div className="spinner"/></div>
            : voters.length === 0
              ? <div style={{ textAlign:'center', padding:40, color:'#9E6040' }}>
                  <div style={{ fontSize:'2.5rem', marginBottom:10 }}>📭</div>
                  <div style={{ fontWeight:700 }}>No voters found</div>
                </div>
              : <div>
                  {/* Column Header */}
                  <div style={{ display:'grid', gridTemplateColumns:'52px 1fr 72px 60px', gap:8, padding:'8px 14px', background:'linear-gradient(135deg,#E55100,#FF6B00)', borderRadius:12, marginBottom:10, color:'white', fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.5px' }}>
                    <span>S.No</span><span>Name / Relative</span><span>Sex / Age</span><span style={{textAlign:'center'}}>Print</span>
                  </div>

                  {voters.map(v => (
                    <div key={v.id} style={{ display:'grid', gridTemplateColumns:'52px 1fr 72px 60px', gap:8, padding:'11px 14px', borderRadius:14, marginBottom:8, background:'rgba(255,255,255,0.9)', border:'1px solid rgba(255,107,0,0.1)', alignItems:'center', boxShadow:'0 2px 10px rgba(0,0,0,0.04)', transition:'all 0.2s' }}
                    onMouseEnter={e=>e.currentTarget.style.borderColor='rgba(255,107,0,0.3)'}
                    onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(255,107,0,0.1)'}>
                      <div style={{ textAlign:'center', fontFamily:"'Baloo 2',sans-serif", fontWeight:800, fontSize:'0.95rem', color:'#E55100' }}>
                        {v.serial_number||'—'}
                      </div>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#1A0A00', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.name}</div>
                        <div style={{ fontSize:'0.73rem', color:'#9E6040', marginTop:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v.relation_name||'—'}</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div className={`badge badge-${v.gender==='M'?'m':'f'}`}>{v.gender}/{v.age}</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <button onClick={() => navigate(`/voter/${v.id}`)}
                          style={{ background:'linear-gradient(135deg,#E55100,#FF6B00)', border:'none', borderRadius:10, padding:'7px 10px', cursor:'pointer', color:'white', fontSize:'1rem', transition:'all 0.2s', boxShadow:'0 3px 10px rgba(255,107,0,0.35)' }}
                          onMouseEnter={e=>e.currentTarget.style.transform='scale(1.1)'}
                          onMouseLeave={e=>e.currentTarget.style.transform='none'}
                          title={`Print ${v.name}`}>
                          🖨️
                        </button>
                      </div>
                    </div>
                  ))}

                  {/* Pagination */}
                  {pages > 1 && (
                    <div className="pagination">
                      <button className="page-btn" onClick={() => loadVoters(selectedBooth, page-1)} disabled={page===1}>‹</button>
                      <span style={{ fontSize:'0.85rem', color:'#9E6040', fontWeight:600, padding:'0 12px' }}>Page {page} of {pages}</span>
                      <button className="page-btn" onClick={() => loadVoters(selectedBooth, page+1)} disabled={page===pages}>›</button>
                    </div>
                  )}
                </div>
        )}
      </div>
    </div>
  );
}